// src/pages/DiscoverPage.tsx
//
// Tinder-style Discover tab. Two modes via a top-level toggle:
//   Feed     — swipeable card deck, top of stack receives gestures
//   Matches  — grid of right-swiped items, tap for detail sheet
//
// Supports deep-linking via ?item=<feed_item_id> query param, which
// reorders the deck so that item is on top (this is what a notification
// tap does).

import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  IconButton,
  Tooltip,
  Badge,
  Chip,
} from '@mui/material';
import {
  UndoOutlined,
  RefreshOutlined,
  ExploreOutlined,
  FavoriteOutlined,
  TuneOutlined,
} from '@mui/icons-material';
import { useFeed, FeedItem, SwipeAction } from '../hooks/useFeed';
import { useGuideManualRefresh } from '../hooks/useGuide';
import { useMatches } from '../hooks/useMatches';
import { SwipeCard, SwipeCardActions } from '../components/discover/SwipeCard';
import { MatchesGrid } from '../components/discover/MatchesGrid';
import { FeedItemDetailSheet } from '../components/discover/FeedItemDetailSheet';
import { DiscoverFiltersSheet } from '../components/discover/DiscoverFiltersSheet';
import { TasteCalibration } from '../components/discover/TasteCalibration';
import { useDiscoverFilters } from '../hooks/useDiscoverFilters';
import { useTasteCalibrationStatus } from '../hooks/useTasteCalibrationStatus';
import { usePullToRefresh } from '../hooks/usePullToRefresh';
import { categoriesForTags } from '../utils/discoverCategories';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useT } from '../i18n/useT';

type Mode = 'feed' | 'matches';
const VISIBLE_STACK = 3; // number of cards rendered at once

export const DiscoverPage: React.FC = () => {
  const t = useT();
  const { items, isLoading, swipe, undoLastSwipe, canUndo } = useFeed();
  const refreshGuide = useGuideManualRefresh();
  const { matches } = useMatches();
  const [mode, setMode] = useState<Mode>('feed');
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Filter + sort state (per-device, persisted to localStorage).
  const { filters, filterAdjustmentCount } = useDiscoverFilters();
  const [filtersSheetOpen, setFiltersSheetOpen] = useState(false);

  // Detail sheet state — opens when user taps a card body.
  const [detailItem, setDetailItem] = useState<FeedItem | null>(null);

  // Cold-start taste calibration. Show once per registered user
  // (anonymous users skip it entirely). The hook reads
  // /users/{uid}.tasteCalibration; if absent → needed=true.
  const calibrationStatus = useTasteCalibrationStatus();

  // Idle auto-refresh. When the user returns to Discover after >5
  // minutes — either by switching back to this tab from another, or
  // by bringing the app back from background — fire a fresh feed
  // refresh in the background. The new content arrives via the
  // existing Firestore listener, no UI jolt.
  const lastActiveRef = useRef<number>(Date.now());
  useEffect(() => {
    const IDLE_THRESHOLD_MS = 5 * 60 * 1000;
    const onVisible = () => {
      if (document.visibilityState !== 'visible') {
        // Going hidden — record the timestamp so when we come back
        // we can compute idle time.
        lastActiveRef.current = Date.now();
        return;
      }
      const idleMs = Date.now() - lastActiveRef.current;
      if (idleMs >= IDLE_THRESHOLD_MS) {
        console.log(
          `[Discover] returning after ${Math.round(idleMs / 1000)}s idle — refreshing`
        );
        refreshGuide();
      }
      lastActiveRef.current = Date.now();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [refreshGuide]);

  // Pipeline:
  //   1. Apply category filter (drop items with no enabled category)
  //   2. Apply hide-closed (drop items KNOWN to be closed; unknowns
  //      stay visible)
  //   3. Sort by user choice (distance / newest / rating)
  //   4. Pin notifications to the top regardless of sort
  //   5. Deep-link reorder: focused item to position 0 if present
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      // Category gate. An item must match at least one enabled
      // category to pass. categoriesForTags always returns ≥1
      // category (falls back to 'other'), so we're checking
      // intersection with the user's enabled set.
      const cats = categoriesForTags(item.tags);
      let categoryMatch = false;
      for (const c of cats) {
        if (filters.enabledCategories.has(c)) {
          categoryMatch = true;
          break;
        }
      }
      if (!categoryMatch) return false;

      // Closed gate. Only hides KNOWN-closed items; an undefined
      // is_open_now (LLM picks, items without place_id) stays.
      if (filters.hideClosed && item.is_open_now === false) return false;

      return true;
    });
  }, [items, filters.enabledCategories, filters.hideClosed]);

  const sortedItems = useMemo(() => {
    const arr = filteredItems.slice();
    arr.sort((a, b) => {
      // Notifications always pin to top regardless of sort. They're
      // time-sensitive surfaces (bell digests) and shouldn't be
      // buried behind hundreds of "closer" items.
      if (a.is_notification !== b.is_notification) {
        return a.is_notification ? -1 : 1;
      }
      switch (filters.sortBy) {
        case 'distance': {
          // Items without a known distance fall to the bottom.
          const aD = typeof a.distance_m === 'number' ? a.distance_m : Infinity;
          const bD = typeof b.distance_m === 'number' ? b.distance_m : Infinity;
          if (aD !== bD) return aD - bD;
          // Tie-break by newest
          return (b.created_at?.getTime() || 0) - (a.created_at?.getTime() || 0);
        }
        case 'rating': {
          // Items without a rating sink. Tie-break by review count
          // (a 4.5 with 5000 reviews is more trustworthy than a 4.6
          // with 3 reviews; sort by rating then by review count
          // accomplishes that for ties).
          const aR = typeof a.rating === 'number' ? a.rating : -1;
          const bR = typeof b.rating === 'number' ? b.rating : -1;
          if (aR !== bR) return bR - aR;
          const aN =
            typeof a.user_ratings_total === 'number'
              ? a.user_ratings_total
              : 0;
          const bN =
            typeof b.user_ratings_total === 'number'
              ? b.user_ratings_total
              : 0;
          return bN - aN;
        }
        case 'newest':
        default: {
          return (
            (b.created_at?.getTime() || 0) - (a.created_at?.getTime() || 0)
          );
        }
      }
    });
    return arr;
  }, [filteredItems, filters.sortBy]);

  // Deep-link: if ?item=<id> arrives, reorder so that item is on top.
  // Also switches to Feed mode.
  const focusItemId = searchParams.get('item');
  const orderedItems = useMemo(() => {
    if (!focusItemId) return sortedItems;
    const idx = sortedItems.findIndex((i) => i.id === focusItemId);
    if (idx <= 0) return sortedItems;
    const focused = sortedItems[idx];
    const rest = sortedItems.filter((_, i) => i !== idx);
    return [focused, ...rest];
  }, [sortedItems, focusItemId]);

  useEffect(() => {
    if (focusItemId) {
      setMode('feed');
    }
  }, [focusItemId]);

  const handleSwipe = (item: FeedItem, action: SwipeAction) => {
    swipe(item, action);
    // Clear deep-link param after the first swipe, so undoing + returning
    // doesn't re-pin the same item on top.
    if (searchParams.get('item')) {
      searchParams.delete('item');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleTapCard = (item: FeedItem) => {
    // Tap opens the detail sheet. Chat is reachable from inside the
    // sheet via the "Ask AI" button, which calls handleAskAIFromSheet.
    setDetailItem(item);
  };

  const handleAskAIFromSheet = (item: FeedItem) => {
    sessionStorage.setItem('instantTours:autoPrompt', item.chat_prompt);
    navigate('/chat', { state: { autoPrompt: item.chat_prompt } });
  };

  const handleOpenChatFromMatch = (chatPrompt: string) => {
    sessionStorage.setItem('instantTours:autoPrompt', chatPrompt);
    navigate('/chat', { state: { autoPrompt: chatPrompt } });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        // Clear the floating frosted bars rendered by AppShell so
        // the top toggle and the bottom swipe/match controls don't
        // get hidden underneath. (Discover is a card-swiper UX, not
        // a scroll list, so we pad the outer rather than letting
        // content extend behind the bars.)
        paddingTop: 'var(--app-header-h)',
        paddingBottom: 'var(--app-bottom-nav-h)',
      }}
    >
      {/* Top toggle */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 1,
          px: 2,
          py: 1,
          borderBottom: 1,
          borderColor: 'divider',
          position: 'relative',
        }}
      >
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, v) => v && setMode(v)}
          size="small"
        >
          <ToggleButton value="feed" sx={{ px: 2 }}>
            <ExploreOutlined sx={{ fontSize: 18, mr: 0.75 }} />
            
            {t("Discover")}
          </ToggleButton>
          <ToggleButton value="matches" sx={{ px: 2 }}>
            <Badge badgeContent={matches.length} color="primary" max={99}>
              <FavoriteOutlined sx={{ fontSize: 18, mr: 0.75 }} />
            </Badge>
            
            {t("Matches")}
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Filter chip — only shown in Feed mode. Sits to the right
            of the toggle group via absolute positioning so it doesn't
            push the centered toggle off-axis. Badge count reflects how
            many adjustments have been made vs defaults. */}
        {mode === 'feed' && (
          <Box
            sx={{
              position: 'absolute',
              right: 12,
              top: '50%',
              transform: 'translateY(-50%)',
            }}
          >
            <Tooltip
              title={
                filterAdjustmentCount > 0
                  ? `Filters · ${filterAdjustmentCount} adjusted`
                  : 'Filter & sort'
              }
            >
              <Badge
                badgeContent={filterAdjustmentCount}
                color="primary"
                overlap="circular"
                invisible={filterAdjustmentCount === 0}
              >
                <IconButton
                  size="small"
                  onClick={() => setFiltersSheetOpen(true)}
                  aria-label={t("Filter and sort")}
                  sx={{
                    border: 1,
                    borderColor:
                      filterAdjustmentCount > 0 ? 'primary.main' : 'divider',
                    color:
                      filterAdjustmentCount > 0
                        ? 'primary.main'
                        : 'text.secondary',
                  }}
                >
                  <TuneOutlined sx={{ fontSize: 18 }} />
                </IconButton>
              </Badge>
            </Tooltip>
          </Box>
        )}
      </Box>

      {mode === 'feed' ? (
        <FeedMode
          items={orderedItems}
          isLoading={isLoading}
          onSwipe={handleSwipe}
          onTap={handleTapCard}
          onUndo={undoLastSwipe}
          canUndo={canUndo}
          onRefresh={refreshGuide}
          rawItemsCount={items.length}
          onOpenFilters={() => setFiltersSheetOpen(true)}
        />
      ) : (
        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          <MatchesGrid onOpenChat={handleOpenChatFromMatch} />
        </Box>
      )}

      {/* Detail sheet — opens when a card is tapped */}
      <FeedItemDetailSheet
        open={!!detailItem}
        item={detailItem}
        onClose={() => setDetailItem(null)}
        onAskAI={handleAskAIFromSheet}
      />

      {/* Filter & sort sheet */}
      <DiscoverFiltersSheet
        open={filtersSheetOpen}
        onClose={() => setFiltersSheetOpen(false)}
      />

      {/* Cold-start taste calibration. Shown once per registered
          user when they first land on Discover with no calibration
          record. Fires the calibrateTaste function which seeds
          /users/{uid}/taste/{tag} with synthetic positive signals
          so the first-day feed has something to weight against. */}
      <TasteCalibration
        open={!calibrationStatus.loading && calibrationStatus.needed && mode === 'feed'}
        onComplete={() => {
          calibrationStatus.dismiss();
          // Trigger a refresh so the user immediately sees a feed
          // calibrated to their selections, not the generic one
          // they were about to see.
          refreshGuide();
        }}
      />
    </Box>
  );
};

const FeedMode: React.FC<{
  items: FeedItem[];
  isLoading: boolean;
  onSwipe: (item: FeedItem, action: SwipeAction) => void;
  onTap: (item: FeedItem) => void;
  onUndo: () => void;
  canUndo: boolean;
  onRefresh: () => void;
  /** How many items the underlying feed has BEFORE filters apply.
   *  When this is > 0 but items.length is 0, the empty state is
   *  caused by filters — show a different message. */
  rawItemsCount: number;
  onOpenFilters: () => void;
}> = ({
  items,
  isLoading,
  onSwipe,
  onTap,
  onUndo,
  canUndo,
  onRefresh,
  rawItemsCount,
  onOpenFilters,
}) => {
  const t = useT();
  const visible = items.slice(0, VISIBLE_STACK);
  const topItem = visible[0] || null;

  // Pull-to-refresh: pull down on the deck to fire a fresh batch.
  // MUST be called unconditionally before any early return below —
  // React's rules of hooks require a stable call count across renders,
  // and `isLoading` flipping false would otherwise add 6 hook calls
  // mid-lifecycle (React error #310). The `disabled` flag inside the
  // hook keeps behavior identical: the gesture is inert in the
  // loading and empty states, where the dedicated Refresh button
  // covers the action better than a gesture would anyway.
  const pull = usePullToRefresh({
    onRefresh,
    disabled: isLoading || items.length === 0,
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography color="text.secondary">{t("Loading the deck…")}</Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    // Filtered to nothing: the deck has items, the user just hid
    // them all with their filter choices. Steer them to the filter
    // sheet, not to refresh.
    if (rawItemsCount > 0) {
      return (
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: 64, mb: 1 }}>🪤</Typography>
          <Typography variant="h6" sx={{ mb: 1 }}>
            
            {t("Nothing matches your filters")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ maxWidth: 320, mx: 'auto', mb: 2 }}
          >
            {rawItemsCount === 1
              ? "There's 1 place in the deck, but it doesn't match your current filters."
              : `There are ${rawItemsCount} places in the deck, but none match your current filters.`}
          </Typography>
          <Button variant="contained" onClick={onOpenFilters}>
            
            {t("Adjust filters")}
          </Button>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          textAlign: 'center',
        }}
      >
        <Typography sx={{ fontSize: 64, mb: 1 }}>🎯</Typography>
        <Typography variant="h6" sx={{ mb: 1 }}>
          
          {t("You're caught up")}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ maxWidth: 320, mx: 'auto', mb: 2 }}
        >
          
          {t("No new places to show right now. The feed refreshes as you move or as time passes. You can also pull in a fresh batch.")}
        </Typography>
        <Button variant="outlined" startIcon={<RefreshOutlined />} onClick={onRefresh}>
          
          {t("Refresh now")}
        </Button>
        {canUndo && (
          <Button
            variant="text"
            startIcon={<UndoOutlined />}
            onClick={onUndo}
            sx={{ mt: 1 }}
          >
            
            {t("Undo last swipe")}
          </Button>
        )}
      </Box>
    );
  }

  // Pull-to-refresh state is computed at the top of FeedMode (before
  // any early return) — see the comment there. `pull` is in scope
  // here for the rendered deck.

  return (
    <Box
      sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        position: 'relative',
        // Translate the entire deck downward as the user pulls so
        // the gesture feels physical. Snaps back instantly on
        // release because pullPx returns to 0.
        transform: pull.pullPx > 0 ? `translateY(${pull.pullPx}px)` : undefined,
        transition: pull.isPulling ? 'none' : 'transform 0.22s ease-out',
        // Prevent the browser's own pull-to-refresh from interfering
        // when the user is at the top of the page. Otherwise on iOS
        // we'd get the page-level overscroll glow on top of ours.
        overscrollBehaviorY: 'contain',
        touchAction: 'pan-y',
      }}
      {...pull.bind}
    >
      {/* Pull indicator — appears at the top of the deck while
          the user is dragging. The icon spins once threshold is
          crossed to telegraph "release will refresh". */}
      {pull.isPulling && pull.pullPx > 8 && (
        <Box
          sx={{
            position: 'absolute',
            top: -44,
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'center',
            pointerEvents: 'none',
            zIndex: 10,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.25,
              py: 0.5,
              borderRadius: 999,
              bgcolor: 'background.paper',
              border: 1,
              borderColor: 'divider',
              boxShadow: 1,
              fontSize: '0.78rem',
              fontWeight: 600,
              color: pull.pullPx >= 80 ? 'success.main' : 'text.secondary',
            }}
          >
            <RefreshOutlined
              sx={{
                fontSize: 16,
                transform: `rotate(${Math.min(360, pull.pullPx * 4)}deg)`,
                transition: 'transform 0.05s linear',
              }}
            />
            {pull.pullPx >= 80 ? 'Release to refresh' : 'Pull to refresh'}
          </Box>
        </Box>
      )}

      {/* Card stack — fills full available height. The action pill
          floats over the bottom of this area as a true overlay. */}
      <Box
        sx={{
          flex: 1,
          position: 'relative',
          px: 2,
          pt: 2,
          pb: 2,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            height: '100%',
            maxWidth: 420,
            mx: 'auto',
          }}
        >
          {/* Render in reverse so the top card is last in DOM = highest z */}
          {visible
            .slice()
            .reverse()
            .map((item, reverseIdx) => {
              const depth = visible.length - 1 - reverseIdx;
              return (
                <SwipeCard
                  key={item.id}
                  item={item}
                  depth={depth}
                  stackSize={visible.length}
                  onSwipe={(action) => onSwipe(item, action)}
                  onTap={() => onTap(item)}
                />
              );
            })}
        </Box>
      </Box>

      {/* Floating action overlay — absolutely positioned so it sits
          ON the card stack area rather than reserving its own height.
          Backdrop blur + semi-transparent surface so cards behind
          show through subtly. */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 12,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          pointerEvents: 'none',
          zIndex: 20,
        }}
      >
        <Box
          sx={{
            pointerEvents: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            px: 1.5,
            py: 0.75,
            borderRadius: 999,
            bgcolor: 'rgba(255,255,255,0.85)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
            border: 1,
            borderColor: 'divider',
          }}
        >
          {/* Undo first — small, secondary visually */}
          <Tooltip title={canUndo ? 'Undo last swipe' : 'Nothing to undo'}>
            <span>
              <IconButton
                size="small"
                disabled={!canUndo}
                onClick={onUndo}
                aria-label={t("Undo last swipe")}
                sx={{ width: 36, height: 36 }}
              >
                <UndoOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          {topItem && (
            <SwipeCardActions
              onPass={() => onSwipe(topItem, 'pass')}
              onMatch={() => onSwipe(topItem, 'match')}
              onSuperMatch={() => onSwipe(topItem, 'super_match')}
              onChat={() => onTap(topItem)}
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};
